import {
  BarChart as MuiBarChart,
  type BarChartProps as MuiBarChartProps,
} from "@mui/x-charts/BarChart";
import { type MakeOptional } from "@mui/x-charts/models/helpers";
import { type BarSeriesType } from "@mui/x-charts/models/seriesType/bar";
import { useTheme } from "@mui/material/styles";
import { fontSize, lineHeight } from "../../theme/tokens";
import { useChartColors } from "./useChartColors";

import { CustomTooltip } from "./CustomTooltip";

export type ForestBarSeriesType = MakeOptional<BarSeriesType, "type"> & {
  /** Enables diagonal striped fill for comparative data representation */
  variant?: "solid" | "striped";
};

export interface BarChartProps extends Omit<MuiBarChartProps, "series"> {
  series?: ForestBarSeriesType[];
}

export function BarChart({ series, colors, ...props }: BarChartProps) {
  const theme = useTheme();
  const chart = theme.palette.chart;
  const seriesColors = useChartColors(series?.length ?? 0);
  const resolvedColors = colors ?? seriesColors;

  // Set default highlight scope for cool hover fading effects, and mount Stripe styling 
  const modifiedSeries = series?.map((s, idx): MakeOptional<BarSeriesType, "type"> => {
    const baseColor = s.color || (resolvedColors as string[])[idx % (resolvedColors as string[]).length];
    const actualColor = s.variant === "striped" ? `url(#striped-pattern-${idx})` : baseColor;

    const { variant, ...rest } = s;

    return {
      ...rest,
      color: actualColor,
      highlightScope: s.highlightScope ?? { highlighted: "item", faded: "global" },
    };
  });

  return (
    <MuiBarChart
      series={modifiedSeries ?? []}
      colors={colors ?? seriesColors}
      grid={{ horizontal: true }}
      borderRadius={4}
      slots={{
        axisContent: CustomTooltip,
      }}
      slotProps={{
        legend: {
          itemMarkWidth: 12,
          itemMarkHeight: 12,
          padding: { bottom: 20 },
        },
      }}
      sx={{
        "& .MuiChartsAxis-line": { stroke: `${chart.axisFg} !important` },
        "& .MuiChartsAxis-tick": { stroke: `${chart.axisFg} !important` },
        "& .MuiChartsAxis-tickLabel": {
          fill: `${chart.labelFg} !important`,
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: `${fontSize.xxs}px !important`,
          lineHeight: lineHeight.xxs,
        },
        "& .MuiChartsAxis-label": {
          fill: `${chart.labelFgEmphasis} !important`,
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: `${typeof theme.typography.body1.fontSize === "number" ? theme.typography.body1.fontSize : 14}px !important`,
          fontWeight: theme.typography.fontWeightMedium,
        },
        "& .MuiChartsGrid-line": {
          stroke: chart.gridlineFg,
          strokeDasharray: "4 4",
        },
        "& .MuiChartsLegend-series text": {
          fill: `${chart.legendFg} !important`,
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: "14px !important",
        },
        "& .MuiChartsLegend-mark": {
          ry: 4,
          rx: 4,
          width: 12,
          height: 12,
        },
        "& .MuiChartsTooltip-root": {
          fontFamily: theme.typography.fontFamily,
          fontSize: typeof theme.typography.body1.fontSize === "number" ? theme.typography.body1.fontSize : 14,
        },
        "& .MuiChartsTooltip-table": {
          backgroundColor: chart.tooltipBg,
          color: chart.tooltipFg,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[4],
        },
        ...((props.sx as object) ?? {}),
      }}
      {...props}
    >
      <defs>
        {series?.map((s, idx) => {
          if (s.variant !== "striped") return null;

          const baseColor = s.color || (resolvedColors as string[])[idx % (resolvedColors as string[]).length];
          return (
            <pattern
              key={`striped-${idx}`}
              id={`striped-pattern-${idx}`}
              patternUnits="userSpaceOnUse"
              width="8"
              height="8"
              patternTransform="rotate(45)"
            >
              <rect width="8" height="8" fill={theme.palette.background.paper} />
              <line x1="0" y="0" x2="0" y2="8" stroke={baseColor} strokeWidth="4" />
            </pattern>
          );
        })}
      </defs>
    </MuiBarChart>
  );
}
